import { ReactElement, ReactNode, useState, useContext, useEffect } from 'react';
import { Formik, Form } from 'formik';
import PhoneNumberField from './Fields/PhoneField';
import BillingInputField from './Fields/BillingInputField';
import Button from '../Button';
import {
  invoiceValidationEN,
  invoiceValidationES,
  preloadPhoneUtil,
} from '../../validations/invoice';
import IdTypeSelect from './Controls/IdTypeSelect';
import { AiOutlineLoading } from 'react-icons/ai';
import { useRouter } from 'next/router';
import { parseCookies } from 'nookies';
import * as Yup from 'yup';
import DynamicField from './Fields/DynamicField';
import { useCustomFields } from '../../hooks/useCustomFields';
import { AuthContext } from '../../helpers/AuthProvider';
import { classifyFreeTicketError } from '../../utils/checkoutBusinessError';


const scrollToFirstError = (formEl: HTMLElement | null) => {
  if (typeof window === 'undefined' || !formEl) return;
  const behavior: ScrollBehavior = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches
    ? 'auto'
    : 'smooth';
  const invalid = formEl.querySelector<HTMLElement>(
    'input.border-red-500, [class*="text-red-7"], [class*="text-red-6"]'
  );
  if (!invalid) return;
  invalid.scrollIntoView({ behavior, block: 'center' });
  const focusEl = formEl.querySelector<HTMLInputElement>('input.border-red-500');
  if (focusEl) {
    try {
      focusEl.focus({ preventScroll: true });
    } catch {
      focusEl.focus();
    }
  }
};

// Orden VISUAL de los campos del form de facturación (móvil) → para elegir el PRIMER campo
// pendiente al pulsar "Comprar".
const BILLING_FIELD_ORDER = [
  'phoneNumber',
  'firstName',
  'lastName',
  'email',
  'idType',
  'idNumber',
  'address',
];

// MÓVIL: desplaza SUAVEMENTE el campo pendiente hasta que quede cómodamente visible, por
// debajo del header sticky y por encima del footer fijo. NO hace focus() → no abre el teclado
// ni dispara el autozoom de Safari. Scrollea dentro del contenedor scrolleable del modal
// (fallback a window). `data-field` localiza el wrapper del campo.
const scrollFieldIntoView = (formEl: HTMLElement | null, fieldName: string) => {
  if (typeof window === 'undefined' || !formEl) return;
  const target = formEl.querySelector<HTMLElement>(`[data-field="${fieldName}"]`);
  if (!target) return;
  const behavior: ScrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth';
  let scroller: HTMLElement | null = target.parentElement;
  while (scroller) {
    const oy = getComputedStyle(scroller).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && scroller.scrollHeight > scroller.clientHeight + 4) break;
    scroller = scroller.parentElement;
  }
  const HEADER_OFFSET = 72; // header sticky (~56px) + holgura
  requestAnimationFrame(() => {
    const tRect = target.getBoundingClientRect();
    if (scroller) {
      const sRect = scroller.getBoundingClientRect();
      const delta = tRect.top - sRect.top - HEADER_OFFSET;
      try {
        scroller.scrollBy({ top: delta, behavior });
      } catch {
        scroller.scrollTop += delta;
      }
    } else {
      const delta = tRect.top - HEADER_OFFSET;
      try {
        window.scrollBy({ top: delta, behavior });
      } catch {
        window.scrollTo(0, window.scrollY + delta);
      }
    }
  });
};

export type BillingInformation = {
  firstName: string;
  lastName: string;
  email: string;
  idType?: string;
  idNumber?: string;
  address?: string;
  phoneNumber: {
    number: string;
    countryDialCode: string;
    countryCode: string;
  };
  orderId: string;
  customFields?: Record<string, any>;
};

type BillingInfoFormProps = {
  eventId: string;
  quantity: number;
  entranceId?: string;
  timeSlotId?: string;
  onSubmit: (x: BillingInformation, e: any) => void;
  initialValues: BillingInformation;
  userEmail?: string;
  isFree?: boolean; // Indicates if the event is free
  space?: SpaceType;
  // 'modal' → compact, clean layout for the desktop "Obtén tu entrada" modal (labels
  // above, flat inputs, 50/50 name row, full-width CTA). SAME Formik logic/validation.
  // 'default' (mobile inline) is untouched.
  variant?: 'default' | 'modal';
};

const BillingInfoForm = ({
  eventId,
  quantity,
  entranceId,
  timeSlotId,
  onSubmit,
  initialValues,
  userEmail,
  isFree = false,
  space,
  variant = 'default',
}: BillingInfoFormProps): ReactElement => {
  const router = useRouter();
  const [submitting, setSubmitting] = useState(false);
  // MÓVIL: campo pendiente resaltado en naranja al intentar comprar con datos faltantes.
  // Se retira solo cuando ese campo vuelve a ser válido (ver flagProps). No usa mensajes.
  const [flaggedField, setFlaggedField] = useState<string | null>(null);
  // Holds a plain string for legacy errors or a title+description node for the
  // sold-out notice; rendered as-is by the existing checkout error box.
  const [apiError, setApiError] = useState<ReactNode>(null);
  const { user } = useContext(AuthContext);

  const cookies = parseCookies();
  const referral = cookies.referral;
  const language = router.locale as SupportedLanguages;
  const isSpanish = language === 'es';

  useEffect(() => {
    const w = window as any;
    const id = w.requestIdleCallback
      ? w.requestIdleCallback(() => preloadPhoneUtil())
      : setTimeout(() => preloadPhoneUtil(), 1200);
    return () => {
      if (w.cancelIdleCallback) w.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, []);

  const {
    customFields,
    loading: customFieldsLoading,
    hasCustomFields,
    getInitialValues: getCustomFieldsInitialValues,
    getValidationSchema: getCustomFieldsValidationSchema,
    extractCustomFieldsData,
  } = useCustomFields({ space, isFree, isSpanish });

  // Create simplified validation schema for free events
  const createFreeEventValidationSchema = () => {
    // A phone is valid only when there are real digits beyond the +57 dial code.
    const phoneHasNumber = (obj: any) => {
      const digits = (obj?.number || '').replace(/\D/g, '');
      const dialDigits = (obj?.countryDialCode || '').replace(/\D/g, '');
      return digits.length > dialDigits.length;
    };
    if (isSpanish) {
      return Yup.object({
        firstName: Yup.string().trim().required('Nombre es un campo requerido.'),
        lastName: Yup.string().trim().required('Apellido es un campo requerido.'),
        email: Yup.string()
          .trim()
          .email('Ingresa un e-mail válido.')
          .required('Email es un campo requerido.'),
        phoneNumber: Yup.object({
          number: Yup.string(),
          countryDialCode: Yup.string(),
          countryCode: Yup.string(),
        }).test('phone-required', 'Celular es un campo requerido.', phoneHasNumber),
      });
    } else {
      return Yup.object({
        firstName: Yup.string().trim().required('First name is a required field.'),
        lastName: Yup.string().trim().required('Last name is a required field.'),
        email: Yup.string()
          .trim()
          .email('Enter a valid e-mail.')
          .required('Email is a required field.'),
        phoneNumber: Yup.object({
          number: Yup.string(),
          countryDialCode: Yup.string(),
          countryCode: Yup.string(),
        }).test('phone-required', 'Phone number is a required field.', phoneHasNumber),
      });
    }
  };

  // Combine validation schemas
  const combinedValidationSchema = () => {
    const baseSchema = isFree
      ? createFreeEventValidationSchema()
      : isSpanish
      ? invoiceValidationES
      : invoiceValidationEN;

    const customFieldsSchema = getCustomFieldsValidationSchema || Yup.object({});

    // Create combined schema by merging the shapes
    return baseSchema.shape(customFieldsSchema.fields || {});
  };

  // Combine initial values
  const getCombinedInitialValues = () => {
    const baseInitialValues = {
      firstName: initialValues.firstName || '',
      lastName: initialValues.lastName || '',
      email: initialValues.email || '',
      idType: initialValues.idType || 'CITIZENSHIP_CARD',
      idNumber: initialValues.idNumber || '',
      address: initialValues.address || '',
      phoneNumber: {
        number: initialValues.phoneNumber?.number || '',
        countryDialCode: initialValues.phoneNumber?.countryDialCode || '',
        countryCode: initialValues.phoneNumber?.countryCode || '',
      },
    };

    const customFieldsInitialValues = getCustomFieldsInitialValues;
    
    return {
      ...baseInitialValues,
      ...customFieldsInitialValues,
    };
  };

  return (
    <div className={variant === 'modal' ? 'w-full' : 'flex flex-col items-center'}>
      <Formik
        initialValues={getCombinedInitialValues()}
        enableReinitialize
        onSubmit={async (values) => {
          setSubmitting(true);
          setApiError(null);
          try {
            const customFieldsData = extractCustomFieldsData(values);

            const paymentPayload = {
              user_phone_number: values.phoneNumber?.number || '',
              user_email: values.email,
              event_id: eventId,
              ...(entranceId ? { entrance_id: entranceId } : {}),
              ...(timeSlotId ? { time_slot_id: timeSlotId } : {}),
              // Free emissions admit exactly one person (one-free-ticket-per-person rule).
              // The UI already locks free quantity to 1; this is belt-and-suspenders so a
              // free request can never send >1. Paid keeps the user's selected quantity.
              number_invitees: isFree ? 1 : Number(quantity),
              referral_code: referral,
              billing_info: values,
              is_free: isFree,
              custom_fields: customFieldsData,
            };
            
            const paymentRes = await fetch('/api/payments/order-id', {
              method: 'POST',
              credentials: 'include',
              body: JSON.stringify(paymentPayload),
              headers: {
                'Content-Type': 'application/json',
              },
            });

            const data = await paymentRes.json();
            const error = data?.error;

            if (!paymentRes.ok || error) {
              // Exhausted free bag: identified by the STABLE backend code (never by
              // exception text). Buyer-safe — no membership/plan/cupo detail. Reuses the
              // existing error box with a Title + Description. Paid checkout never returns
              // this code, so it is unaffected.
              if (data?.error_code === 'FREE_EMISSION_LIMIT_REACHED') {
                setApiError(
                  <>
                    <p className="font-semibold">
                      {isSpanish ? 'Este acceso está agotado' : 'This pass is sold out'}
                    </p>
                    <p className="mt-0.5">
                      {isSpanish
                        ? 'Por el momento no hay entradas disponibles. Puedes contactar al organizador para obtener más información.'
                        : 'There are no tickets available at the moment. You can contact the organizer for more information.'}
                    </p>
                  </>
                );
                setSubmitting(false);
                return;
              }
              const message = String(data?.message ?? '');

              // One-free-ticket-per-person rule (backend). Mapped by STABLE code (409) or,
              // defensively, the single-admission 400 — never by generic status. Buyer-safe:
              // no QR, no prior ticket, no UUID/JSON; just guidance to check email/WhatsApp.
              // No "resend" button yet — that flow does not exist. Paid checkout never hits this.
              const freeBusinessError = classifyFreeTicketError({
                errorCode: data?.error_code,
                message,
                isFree,
              });
              if (freeBusinessError === 'FREE_TICKET_ALREADY_ISSUED') {
                setApiError(
                  <>
                    <p className="font-semibold">
                      {isSpanish
                        ? 'Ya tienes una entrada gratuita para este evento'
                        : 'You already have a free ticket for this event'}
                    </p>
                    <p className="mt-0.5">
                      {isSpanish
                        ? 'Revisa tu correo o WhatsApp para encontrarla.'
                        : 'Check your email or WhatsApp to find it.'}
                    </p>
                  </>
                );
                setSubmitting(false);
                return;
              }
              if (freeBusinessError === 'FREE_SINGLE_ADMISSION') {
                setApiError(
                  <>
                    <p className="font-semibold">
                      {isSpanish
                        ? 'Solo una entrada gratuita por persona'
                        : 'Only one free ticket per person'}
                    </p>
                    <p className="mt-0.5">
                      {isSpanish
                        ? 'Las entradas gratuitas están limitadas a una por persona.'
                        : 'Free tickets are limited to one per person.'}
                    </p>
                  </>
                );
                setSubmitting(false);
                return;
              }

              const isReferralError =
                referral &&
                (message.toLowerCase().includes('referral') ||
                  message.toLowerCase().includes('referido'));
              const isNoSubscription = message.toLowerCase().includes('subscription');
              setApiError(
                isReferralError
                  ? isSpanish
                    ? `El código de referido "${referral}" no es válido o no existe.`
                    : `The referral code "${referral}" is invalid or does not exist.`
                  : isNoSubscription
                  ? isSpanish
                    ? 'El organizador de este evento no tiene una suscripción activa para crear entradas.'
                    : 'The event organizer does not have an active subscription to create tickets.'
                  : message || (isSpanish
                  ? 'Ocurrió un error al procesar tu solicitud. Intenta de nuevo.'
                  : 'An error occurred processing your request. Please try again.')
              );
              setSubmitting(false);
              return;
            }

            if (user) {
              try {
                await fetch(`/api/billing-info`, {
                  headers: { 'Content-Type': 'application/json' },
                  method: 'POST',
                  credentials: 'include',
                  body: JSON.stringify({
                    ...values,
                    userEmail,
                    customFields: customFieldsData,
                  }),
                });
              } catch (e) {
                console.warn('Billing info storage failed:', e);
              }
            }

            onSubmit(
              {
                ...values,
                orderId: data?.payment?.reference,
                customFields: customFieldsData,
              },
              error
            );
          } catch (e) {
            setApiError(
              isSpanish
                ? 'Ocurrió un error al procesar tu solicitud. Intenta de nuevo.'
                : 'An error occurred processing your request. Please try again.'
            );
          }
          setSubmitting(false);
        }}
        validationSchema={combinedValidationSchema()}
      >
        {({ handleSubmit, validateForm, errors }) => {
          // --- Compact, clean MODAL variant (desktop "Obtén tu entrada") -------------
          // Reuses the exact same Formik state/validation/submit above; only the
          // presentation differs: labels above, flat inputs, 50/50 name row, full CTA.
          if (variant === 'modal') {
            // Label de los campos (Nombre, Correo, Dirección, …) = estilo de referencia:
            // 14px, peso normal, color #151515A3 (#151515 @ ~64% alpha). Fuente Roboto de
            // Tikipal (no la system-font de la captura). El "*" requerido sigue naranja.
            const labelCls = 'block mb-1 text-[14px] font-normal text-[#151515A3]';
            return (
              <Form className="w-full">
                {/* 1 sola columna (estilo Luma): cada campo ocupa una fila completa */}
                <div>
                  <div>
                    <label className={labelCls}>{isSpanish ? 'Nombre' : 'First name'} <span className="text-tikipal-orange">*</span></label>
                    <BillingInputField name="firstName" placeholder={isSpanish ? 'Ej. Camilo' : 'e.g. James'} labeled={false} flat />
                  </div>
                  <div>
                    <label className={labelCls}>{isSpanish ? 'Apellido' : 'Last name'} <span className="text-tikipal-orange">*</span></label>
                    <BillingInputField name="lastName" placeholder={isSpanish ? 'Ej. Acosta' : 'e.g. Smith'} labeled={false} flat />
                  </div>
                  <div>
                    <label className={labelCls}>{isSpanish ? 'Celular' : 'Phone'} <span className="text-tikipal-orange">*</span></label>
                    <PhoneNumberField name="phoneNumber" label={isSpanish ? 'Celular' : 'Phone'} labeled={false} flat />
                  </div>
                  <div>
                    <label className={labelCls}>E-mail <span className="text-tikipal-orange">*</span></label>
                    <BillingInputField name="email" placeholder={isSpanish ? 'correo@ejemplo.com' : 'name@example.com'} type="email" labeled={false} flat />
                  </div>
                </div>

                {/* Paid-only fields (free flow never shows these; kept functional).
                    MISMO sistema visual que los demás campos del modal: label arriba
                    (labelCls) + input/select flat. */}
                {!isFree && (
                  <div>
                    <div>
                      <label className={labelCls}>{isSpanish ? 'Tipo de documento' : 'ID type'} <span className="text-tikipal-orange">*</span></label>
                      <IdTypeSelect name="idType" flat />
                    </div>
                    <div>
                      <label className={labelCls}>{isSpanish ? 'No. Identificación' : 'ID Number'} <span className="text-tikipal-orange">*</span></label>
                      <BillingInputField name="idNumber" placeholder={isSpanish ? 'Ej. 1020765527' : 'e.g. 1020765527'} type="number" labeled={false} flat />
                    </div>
                    <div>
                      <label className={labelCls}>{isSpanish ? 'Dirección' : 'Address'} <span className="text-tikipal-orange">*</span></label>
                      <BillingInputField name="address" placeholder={isSpanish ? 'Ej. Calle 85 # 12-34' : 'e.g. 85th St # 12-34'} labeled={false} flat />
                    </div>
                  </div>
                )}

                {hasCustomFields && (
                  <div className="w-full mt-1 pt-3 border-t border-gray-100">
                    {customFieldsLoading ? (
                      <div className="flex justify-center"><AiOutlineLoading className="animate-spin text-copper text-lg" /></div>
                    ) : (
                      <div className="space-y-3">
                        {customFields.map((field) => (<DynamicField key={field.id} field={field} />))}
                      </div>
                    )}
                  </div>
                )}

                {apiError && (
                  <div className="w-full mb-3 rounded-lg bg-red-50 border border-red-200 px-4 py-2.5 text-[13px] text-red-700 text-center">
                    {apiError}
                  </div>
                )}

                <button
                  type="button"
                  id="billing-info-form-submit"
                  onClick={(e: any) => {
                    const formEl = (e.currentTarget as HTMLElement).closest('form');
                    handleSubmit(e);
                    setTimeout(() => scrollToFirstError(formEl), 60);
                  }}
                  disabled={submitting}
                  className="w-full h-[44px] mt-1.5 rounded-[12px] bg-tikipal-orange text-white text-[14px] font-semibold hover:bg-copper-600 active:bg-copper-700 transition-colors disabled:opacity-60 flex items-center justify-center"
                >
                  {submitting ? (
                    <AiOutlineLoading className="animate-spin text-white text-base" />
                  ) : isFree ? (
                    isSpanish ? 'Obtener entrada' : 'Get ticket'
                  ) : (
                    isSpanish ? 'Comprar' : 'Buy tickets'
                  )}
                </button>

                <p className="text-[11px] mt-2.5 text-center text-gray-500 leading-snug [text-wrap:balance]">
                  {isSpanish ? (
                    <>Al enviar tu información aceptas los{' '}
                      <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="text-tikipal-orange underline">términos y condiciones</a>{' '}
                      de los datos registrados en este sitio.</>
                  ) : (
                    <>By sending your information you accept the{' '}
                      <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="text-tikipal-orange underline">terms and conditions</a>{' '}
                      of the data registered on this site.</>
                  )}
                </p>
              </Form>
            );
          }

          // Labels ARRIBA + asterisco naranja + inputs `flat` (MISMO tratamiento visual que el
          // checkout Desktop: fondo gris sutil booking-field-fill, borde suave, radio 12, sin
          // mensaje de error inline). Orden de campos INTACTO (Celular primero). La clase
          // `checkout-form-mobile` aplica el relieve sutil scopeado SOLO a Mobile (globals.css).
          const labelCls = 'block mb-1 text-[14px] font-normal text-[#151515A3]';
          // Resalta en NARANJA solo el PRIMER campo pendiente; `data-field` localiza el wrapper
          // para el scroll. Se retira solo cuando el campo vuelve a ser válido (errors[name]
          // desaparece con la validación en vivo de Formik). Sin mensajes, sin rojo.
          const flagProps = (name: string) => ({
            'data-field': name,
            className:
              flaggedField === name && (errors as Record<string, unknown>)[name]
                ? 'tikipal-field-flag'
                : undefined,
          });
          return (
            <Form className="w-full checkout-form-mobile">
              <div {...flagProps('phoneNumber')}>
                <label className={labelCls}>{isSpanish ? 'Celular' : 'Phone'} <span className="text-tikipal-orange">*</span></label>
                <PhoneNumberField name="phoneNumber" label={isSpanish ? 'Celular' : 'Phone'} labeled={false} flat />
              </div>
              <div {...flagProps('firstName')}>
                <label className={labelCls}>{isSpanish ? 'Nombre' : 'First name'} <span className="text-tikipal-orange">*</span></label>
                <BillingInputField name="firstName" placeholder={isSpanish ? 'Ej. Camilo' : 'e.g. James'} labeled={false} flat />
              </div>
              <div {...flagProps('lastName')}>
                <label className={labelCls}>{isSpanish ? 'Apellido' : 'Last name'} <span className="text-tikipal-orange">*</span></label>
                <BillingInputField name="lastName" placeholder={isSpanish ? 'Ej. Acosta' : 'e.g. Smith'} labeled={false} flat />
              </div>
              <div {...flagProps('email')}>
                <label className={labelCls}>E-mail <span className="text-tikipal-orange">*</span></label>
                <BillingInputField name="email" placeholder={isSpanish ? 'correo@ejemplo.com' : 'name@example.com'} type="email" labeled={false} flat />
              </div>

              {/* Paid-only fields (free flow never shows these; kept functional). Mismo sistema visual. */}
              {!isFree && (
                <>
                  <div {...flagProps('idType')}>
                    <label className={labelCls}>{isSpanish ? 'Tipo de documento' : 'ID type'} <span className="text-tikipal-orange">*</span></label>
                    <IdTypeSelect name="idType" flat />
                  </div>
                  <div {...flagProps('idNumber')}>
                    <label className={labelCls}>{isSpanish ? 'No. Identificación' : 'ID Number'} <span className="text-tikipal-orange">*</span></label>
                    <BillingInputField name="idNumber" placeholder={isSpanish ? 'Ej. 1020765527' : 'e.g. 1020765527'} type="number" labeled={false} flat />
                  </div>
                  <div {...flagProps('address')}>
                    <label className={labelCls}>{isSpanish ? 'Dirección' : 'Address'} <span className="text-tikipal-orange">*</span></label>
                    <BillingInputField name="address" placeholder={isSpanish ? 'Ej. Calle 85 # 12-34' : 'e.g. 85th St # 12-34'} labeled={false} flat />
                  </div>
                </>
              )}

              {/* Custom Fields Section */}
              {hasCustomFields && (
                <div className="w-full mt-4 p-4 border-t border-gray-200">
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">
                    {isSpanish ? 'Información adicional' : 'Additional Information'}
                  </h3>
                  {customFieldsLoading ? (
                    <div className="flex justify-center">
                      <AiOutlineLoading className="animate-spin text-copper text-lg" />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {customFields.map((field) => (
                        <DynamicField key={field.id} field={field} />
                      ))}
                    </div>
                  )}
                </div>
              )}


              {apiError && (
                <div className="w-full mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 text-center">
                  {apiError}
                </div>
              )}
              <div className="hidden lg:flex pb-6 w-full justify-center">
                <Button
                  onClick={async (e: any) => {
                    // Evita cualquier submit/validación nativa que bloquee nuestro scroll.
                    e?.preventDefault?.();
                    const formEl = (e.currentTarget as HTMLElement).closest('form');
                    // Validación COMPLETA (mismas reglas). validateForm() no marca touched,
                    // así que no aparecen bordes rojos ni mensajes en otros campos.
                    const errs = await validateForm();
                    const names = Object.keys(errs || {});
                    if (names.length === 0) {
                      setFlaggedField(null);
                      handleSubmit(); // todo válido → flujo de pago existente
                      return;
                    }
                    // PRIMER campo pendiente por orden visual → resaltar (naranja) + scroll
                    // suave hasta dejarlo visible. SIN focus() → sin teclado, sin autozoom.
                    const first =
                      BILLING_FIELD_ORDER.find((n) => (errs as Record<string, unknown>)[n]) ||
                      names[0];
                    setFlaggedField(first);
                    scrollFieldIntoView(formEl, first);
                  }}
                  disabled={submitting}
                  id="billing-info-form-submit"
                >
                  {submitting ? (
                    <div className="w-full flex justify-center items-center h-5">
                      <AiOutlineLoading className="animate-spin text-white text-sm" />
                    </div>
                  ) : <p className='text-xl'>
                    {isFree ? isSpanish ? (
                      'Obtener entrada'
                    ) : (
                      'Get ticket'
                    ) : isSpanish ? (
                      'Comprar'
                    ) : (
                      'Buy tickets'
                    )}
                  </p>}
                </Button>
              </div>
              {isSpanish ? (
                <div className="text-xs text-center pb-6 text-gray-400">
                  Al enviar tu información aceptas los{' '}
                  <a
                    href="/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-copper underline"
                  >
                    términos y condiciones
                  </a>{' '}
                  de los datos registrados en este sitio.
                </div>
              ) : (
                <div className="text-xs text-center pb-6 text-gray-400">
                  By sending your information you accept the{' '}
                  <a
                    href="/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-copper underline"
                  >
                    terms and conditions
                  </a>{' '}
                  of the data registered on this site.
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default BillingInfoForm;
